import { useEffect, useSyncExternalStore } from 'react';

/**
 * Pages that want the phone tab bar out of the way while they are on screen.
 *
 * A focused task (an open chat conversation) runs full height on a phone,
 * the way messaging apps hide their tab bar inside a thread, and comes back
 * to the tab bar on the way out. A count rather than a flag, so two
 * components asking at once cannot clear each other.
 */
let requests = 0;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

const subscribe = (onChange: () => void) => {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};

/** Hide the tab bar while `active` is true and the caller is mounted. */
export function useHideTabBar(active: boolean) {
  useEffect(() => {
    if (!active) return;
    requests += 1;
    notify();
    return () => {
      requests -= 1;
      notify();
    };
  }, [active]);
}

/** Read by the shell. */
export function useTabBarSuppressed(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => requests > 0,
    () => false,
  );
}
